import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ResourceCard from "@/components/feature/ResourceCard";
import usePageMeta from "@/hooks/usePageMeta";
import { animeResources, categories } from "@/mocks/resources";

export default function Library() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  usePageMeta(
    "香蕉动漫资源库 - 动漫全集资源合集目录",
    "香蕉动漫资源库，汇总热门动漫全集资源与资源站目录，支持按主题分类与关键词搜索，一站找齐你想看的动漫合集，追更不迷路。",
    "/library",
  );

  const allCount = animeResources.length;

  const filtered = useMemo(() => {
    const catName = categories.find((c) => c.id === activeCat)?.name ?? "";
    let list = [...animeResources];
    if (catName) list = list.filter((r) => r.category === catName);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.category.includes(q) ||
          r.tags.some((t) => t.includes(q)),
      );
    }
    return list.sort((a, b) => b.updateTime.localeCompare(a.updateTime));
  }, [activeCat, query]);

  const selectCat = (id: string) => {
    if (id === "all") searchParams.delete("cat");
    else searchParams.set("cat", id);
    setSearchParams(searchParams);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (keyword.trim()) searchParams.set("q", keyword.trim());
    else searchParams.delete("q");
    setSearchParams(searchParams);
  };

  const currentCatName = activeCat === "all" ? "全部" : categories.find((c) => c.id === activeCat)?.name;

  return (
    <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 pb-4">
      {/* 面包屑 */}
      <nav aria-label="面包屑" className="text-sm text-foreground-600">
        <ol className="flex items-center gap-1 flex-wrap">
          <li><a href="/" className="hover:text-primary-600">首页</a></li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
          <li aria-current="page" className="text-foreground-950 font-medium">资源库</li>
        </ol>
      </nav>

      <div className="mt-6 flex flex-col lg:flex-row gap-8">
        {/* 侧栏分类 */}
        <aside className="w-full lg:w-64 shrink-0">
          <div className="lg:sticky lg:top-24">
            <h2 className="font-heading text-lg text-foreground-950">资源分类</h2>
            <nav className="mt-4 space-y-1.5" aria-label="资源分类">
              <button
                type="button"
                onClick={() => selectCat("all")}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors cursor-pointer ${
                  activeCat === "all"
                    ? "bg-primary-500 text-foreground-950 font-medium"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-apps-2-line" aria-hidden="true"></i> 全部资源
                </span>
                <span className="text-xs">{allCount}</span>
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectCat(c.id)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors cursor-pointer ${
                    activeCat === c.id
                      ? "bg-primary-500 text-foreground-950 font-medium"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <i className={c.icon} aria-hidden="true"></i> {c.name}
                  </span>
                  <span className="text-xs">{c.count}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* 主内容 */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                {currentCatName} · 动漫资源合集
              </h1>
              <p className="mt-2 text-sm text-foreground-700">
                共找到 <strong className="text-primary-700">{filtered.length}</strong> 部资源
              </p>
            </div>
            <form
              onSubmit={submitSearch}
              className="sm:w-72 flex items-center gap-2 bg-background-100 rounded-full px-4 py-2"
              role="search"
            >
              <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索动漫名称"
                className="w-full bg-transparent text-sm outline-none text-foreground-950 placeholder:text-foreground-500"
              />
            </form>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((r) => (
                <ResourceCard key={r.id} resource={r} />
              ))}
            </div>
          ) : (
            <div className="mt-10 py-16 text-center bg-background-100/70 rounded-2xl">
              <i className="ri-inbox-line text-4xl text-foreground-400" aria-hidden="true"></i>
              <p className="mt-4 text-sm text-foreground-700">没有找到匹配的资源，换个关键词试试吧</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}